/* eslint-disable no-console */
/* eslint-disable fp/no-delete */
import { pipe } from 'fp-ts/function';
import lodash from 'lodash';
import { v4 as uuid } from 'uuid';

import type { CollapsibleListData_InitialExpandedItems, Node, RichContent } from './../../../../ricos-schema/legacy/wix/rich_content/v1';
import { ButtonData_Type, Node_Type } from './../../../../ricos-schema/legacy/wix/rich_content/v1';

import {
  ACTION_BUTTON_TYPE,
  LINK_BUTTON_TYPE,
  LINK_PREVIEW_TYPE,
  RICOS_NODE_TYPE_TO_DATA_FIELD,
} from '../../../consts';
import type { DraftContent, RicosContentBlock, RicosEntityMap } from '../../../types';
import type { DraftBlockType } from '../../../types/draft-js';
import { Version } from '../../../version';
import { generateId } from '../../generateRandomId';
import {
  BlockType,
  FROM_RICOS_ENTITY_TYPE,
  HeaderLevel,
  TO_DRAFT_LIST_TYPE,
  TO_RICOS_DATA_FIELD,
} from '../consts';
import { convertContainerData } from './convertContainerData';
import { converters as nodeConverters } from './convertDraftPluginDataNoInnerRCE';
import { createEntity } from './create-entity';
import {
  getParagraphNode,
  mergeParagraphNodes,
  mergeTextNodes,
  parseDecorations,
  parseEntityDecorations,
  parseInlineStyleDecorations,
} from './decorationParsers';
import { createTextBlockData } from './getDraftEntityData';
import preprocess from './preprocess';
import parseDocStyle from './utils/parseDocStyle';

const getNodeEntityData = (node: Node) => {
  const { type } = node;
  let draftPluginType = FROM_RICOS_ENTITY_TYPE[type];
  if (type === Node_Type.BUTTON) {
    draftPluginType =
      node.buttonData?.type === ButtonData_Type.ACTION ? ACTION_BUTTON_TYPE : LINK_BUTTON_TYPE;
  } else if (type === Node_Type.EMBED) {
    draftPluginType = LINK_PREVIEW_TYPE;
  }
  const data = convertNodeToDraftData(node);
  if (data === undefined) {
    // eslint-disable-next-line no-console
    throw Error(`ERROR! Unknown entity type "${type}"!`);
  }
  return { type: draftPluginType, data };
};

const createAtomicEntityData = (node: Node, entityKey: number): RicosEntityMap => {
  const { type, data } = getNodeEntityData(node);
  return createEntity(entityKey, { type, mutability: 'IMMUTABLE', data });
};

const convert =
  (options = { ignoreUnsupportedTypes: false }) =>
  (ricosContent: RichContent): DraftContent => {
    const { nodes, documentStyle } = ricosContent;

    const draftContent: DraftContent = {
      blocks: [],
      entityMap: {},
    };
    let latestEntityKey = -1;
    const usedKeys: Record<string, boolean> = {};

    const parseNodes = () => {
      nodes?.forEach((node, index) => {
        const prevNode = nodes[index - 1];
        if (node) {
          switch (node.type) {
            case Node_Type.BLOCKQUOTE:
              parseTextNodes(getParagraphNode(node), {
                type: BlockType.Blockquote,
                key: node.id,
                indentation: node.blockquoteData?.indentation,
              });
              break;
            case Node_Type.CODE_BLOCK:
              parseTextNodes(node, { type: BlockType.CodeBlock, key: node.id });
              break;
            case Node_Type.HEADING:
              if (!node.headingData) {
                throw Error(`ERROR! Heading node with no data!`);
              }
              parseTextNodes(node, { type: HeaderLevel[node.headingData.level], key: node.id });
              break;
            case Node_Type.ORDERED_LIST:
            case Node_Type.BULLETED_LIST:
              // if there are multiple lists in a row, we need to add a new block to separate them
              if (prevNode?.type === node.type) {
                addBlock({ type: BlockType.Atomic });
              }
              parseListNode(node);
              break;
            case Node_Type.PARAGRAPH:
              parseTextNodes(node, { type: BlockType.Unstyled, key: node.id });
              break;
            default:
              if (RICOS_NODE_TYPE_TO_DATA_FIELD[node.type]) {
                parseAtomicNode(node);
              } else if (!options.ignoreUnsupportedTypes) {
                throw Error(`ERROR! Unknown node type "${node.type}"!`);
              }
          }
        }
      });
    };

    const parseAtomicNode = (node: Node) => {
      latestEntityKey += 1;
      const entityMap = createAtomicEntityData(node, latestEntityKey);
      addBlock({
        key: node.id,
        type: BlockType.Atomic,
        text: ' ',
        entityRanges: [{ offset: 0, length: 1, key: latestEntityKey }],
      });
      draftContent.entityMap = { ...draftContent.entityMap, ...entityMap };
    };

    const parseListNode = (node: Node, indent?: number) => {
      const indentation =
        node.bulletedListData?.indentation ?? node.orderedListData?.indentation ?? indent ?? 0;
      node.nodes?.forEach(listItem => {
        parseParagraphNodes(listItem, {
          type: TO_DRAFT_LIST_TYPE[node.type],
          key: listItem?.nodes?.[0].id ?? '',
          indentation,
        });

        const nestedListItem = listItem.nodes?.find(
          node => node.type === Node_Type.ORDERED_LIST || node.type === Node_Type.BULLETED_LIST
        );

        if (nestedListItem) {
          parseListNode(nestedListItem, indentation + 1);
        }
      });
    };

    const parseParagraphNodes = (
      node: Node,
      { type, key, indentation }: { type: DraftBlockType; key: string; indentation?: number }
    ) => {
      const { text, decorationMap } = mergeParagraphNodes(node?.nodes ?? []);

      const { inlineStyleDecorations, entityDecorations } = parseDecorations(decorationMap, text);
      const inlineStyleRanges = parseInlineStyleDecorations(inlineStyleDecorations);
      const {
        entityRanges,
        entityMap,
        latestEntityKey: newLatestEntityKey,
      } = parseEntityDecorations(entityDecorations, latestEntityKey);
      latestEntityKey = newLatestEntityKey;
      const firstChild = node.nodes?.[0];
      if (!firstChild) {
        return;
      }
      const { depth, ...data } = createTextBlockData(firstChild);

      addBlock({
        key,
        type,
        text,
        depth: indentation || depth,
        inlineStyleRanges,
        entityRanges,
        data,
      });
      draftContent.entityMap = { ...draftContent.entityMap, ...entityMap };
    };

    const parseTextNodes = (
      node: Node,
      { type, key, indentation }: { type: DraftBlockType; key: string; indentation?: number }
    ) => {
      const { text, decorationMap } = mergeTextNodes(node.nodes);
      const { inlineStyleDecorations, entityDecorations } = parseDecorations(decorationMap, text);
      const inlineStyleRanges = parseInlineStyleDecorations(inlineStyleDecorations);
      const {
        entityRanges,
        entityMap,
        latestEntityKey: newLatestEntityKey,
      } = parseEntityDecorations(entityDecorations, latestEntityKey);
      latestEntityKey = newLatestEntityKey;
      const { depth, ...data } = createTextBlockData(node);
      addBlock({
        key,
        type,
        text,
        depth: indentation || depth,
        inlineStyleRanges,
        entityRanges,
        data,
      });
      draftContent.entityMap = { ...draftContent.entityMap, ...entityMap };
    };

    const addBlock = (blockProps?: Partial<RicosContentBlock>) => {
      const newBlock: RicosContentBlock = lodash.merge(
        {
          key: generateId(),
          type: BlockType.Unstyled,
          text: '',
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {},
        },
        blockProps
      );
      if (usedKeys[newBlock.key] && newBlock.key !== '') {
        console.warn(`WARN! Duplicate block key "${newBlock.key}"!`);
        newBlock.key = generateId();
      }
      usedKeys[newBlock.key] = true;
      draftContent.blocks = [...draftContent.blocks, newBlock];
    };

    parseNodes();
    documentStyle && (draftContent.documentStyle = parseDocStyle(documentStyle));
    draftContent.VERSION = Version.currentVersion;
    draftContent.ID = ricosContent.metadata?.id || uuid();
    return draftContent;
  };

const convertCollapsibleListData = (
  data: {
    config: {
      expandState?: string;
      expandOnlyOne?: boolean;
      direction?: string;
      isQapageData?: boolean;
      alignment?: string;
    };
    initialExpandedItems?: CollapsibleListData_InitialExpandedItems;
    expandOnlyOne?: boolean;
    isQapageData?: boolean;
    direction?: string;
    pairs: $TSFixMe;
  },
  nodes: Node[]
) => {
  const { initialExpandedItems, expandOnlyOne, direction, isQapageData } = data || {};

  const getExpandState = (initialExpandedItems?: CollapsibleListData_InitialExpandedItems) => {
    if (initialExpandedItems === 'ALL') {
      return 'expanded';
    }
    if (initialExpandedItems === 'NONE') {
      return 'collapsed';
    }
    return 'first_expanded';
  };

  const config = {
    expandState: getExpandState(initialExpandedItems),
    expandOnlyOne,
    isQapageData,
    direction: direction?.toLowerCase(),
  };
  data.config = { ...data.config, ...config };
  data.pairs = nodes.map(node => {
    const [first, second] = node.nodes ?? [];
    if (!first || !second) {
      throw new Error('Collapsible list pair must have 2 nodes');
    }
    const { VERSION: _, ID: __, ...title } = toDraft(first);
    const { VERSION: ___, ID: ____, ...content } = toDraft(second);
    return {
      key: node.id,
      title,
      content,
    };
  });
  delete data.initialExpandedItems;
  delete data.expandOnlyOne;
  delete data.isQapageData;
  delete data.direction;
  delete data.config.alignment;
};

const convertTableData = (
  data: {
    dimensions: $TSFixMe;
    header: $TSFixMe;
    rowHeader: $TSFixMe;
    columnHeader: $TSFixMe;
    config: {
      colsWidth: $TSFixMe;
      rowsHeight: $TSFixMe;
      colsMinWidth: $TSFixMe;
      alignment?: $TSFixMe;
      rows: $TSFixMe;
      rowHeader: $TSFixMe;
      colHeader: $TSFixMe;
    };
  },
  nodes: $TSFixMe
) => {
  const {
    dimensions: { colsWidthRatio, rowsHeight, colsMinWidth },
    header,
    rowHeader,
    columnHeader,
  } = data || {};
  const { alignment: _, ...rest } = data.config || {};

  data.config = {
    ...rest,
    colsWidth: colsWidthRatio,
    rowsHeight,
    colsMinWidth,
    rowHeader: rowHeader ?? header,
    colHeader: columnHeader,
  };
  const rows: $TSFixMe = {};
  nodes.forEach((row: $TSFixMe, i: $TSFixMe) => {
    rows[i] = { columns: {} };
    row.nodes.forEach((cell: $TSFixMe, j: $TSFixMe) => {
      const { VERSION: _, ID: __, ...content } = toDraft(cell);
      const { cellStyle = {}, borderColors = {} } = cell.tableCellData || {};
      rows[i].columns[j] = {
        content,
        style: {
          verticalAlign: cellStyle.verticalAlignment?.toLowerCase(),
          backgroundColor: cellStyle.backgroundColor?.toLowerCase(),
        },
        border: {
          top: borderColors.top?.toLowerCase(),
          left: borderColors.left?.toLowerCase(),
          right: borderColors.right?.toLowerCase(),
          bottom: borderColors.bottom?.toLowerCase(),
        },
      };
    });
  });
  data.config.rows = rows;
  delete data.dimensions;
  delete data.header;
  delete data.rowHeader;
  delete data.columnHeader;
};

export const convertNodeToDraftData = (node: Node) => {
  const { type } = node;
  const draftPluginType = FROM_RICOS_ENTITY_TYPE[type];
  const dataFieldName = TO_RICOS_DATA_FIELD[draftPluginType];
  if (!dataFieldName) {
    console.error(`No data field name | Plugin Name: ${draftPluginType}`);
  }
  return convertNodeDataToDraft(
    type,
    node[dataFieldName as $TSFixMeKey<typeof node>] || {},
    node.nodes
  );
};

export const convertNodeDataToDraft = (nodeType: Node_Type, data: $TSFixMe, nodes?: Node[]) => {
  if (!data) {
    if (
      ![
        Node_Type.COLLAPSIBLE_ITEM,
        Node_Type.COLLAPSIBLE_ITEM_BODY,
        Node_Type.COLLAPSIBLE_ITEM_TITLE,
      ].includes(nodeType)
    ) {
      console.error(`No data for ${nodeType}`);
    }
    return {};
  }
  const converters = {
    ...nodeConverters,
    [Node_Type.COLLAPSIBLE_LIST]: convertCollapsibleListData,
    [Node_Type.TABLE]: convertTableData,
  };

  const newData = {
    ...lodash.cloneDeep(data),
    config: convertContainerData(data, nodeType),
    containerData: undefined,
  };

  if (nodeType in converters) {
    const convert = converters[nodeType as $TSFixMeKey<typeof converters>];
    convert(newData, nodes);
  }

  return JSON.parse(JSON.stringify(newData)); // remove undefined values
};

export const toDraft = (content: RichContent, options = { ignoreUnsupportedTypes: false }) =>
  pipe(content, preprocess, convert(options));

export const ensureDraftContent = (content: RichContent | DraftContent): DraftContent =>
  'nodes' in content ? toDraft(content) : content;
